/**
 * Telegram Mini App bootstrap.
 *
 * A hand-written wrapper over `window.Telegram.WebApp` rather than a
 * dependency. We need six things — `initData`, the theme palette, the viewport
 * height, `ready`/`expand`, the back button, and haptics — and the official
 * SDK's surface churns considerably between majors for what amounts to a
 * hundred lines of property reads. The rest of this repo is deliberately
 * dependency-light (pg, fastify, zod, react) and this is the same judgement.
 *
 * **`initData` is an opaque credential.** It is never parsed here and nothing
 * in it is trusted: `telegram_id`, the username, the language are all read from
 * the *server's* verified copy after `POST /auth/session`. §7.1 is explicit
 * that a client-supplied `telegram_id` is the impersonation hole, and a client
 * that decoded its own initData for convenience would be one refactor away from
 * sending the decoded value somewhere that mattered. The one exception is the
 * language hint below, which decides which words to display and can safely be
 * wrong.
 */

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  destructive_text_color?: string;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { user?: { language_code?: string }; start_param?: string };
  colorScheme?: 'light' | 'dark';
  themeParams?: ThemeParams;
  viewportStableHeight?: number;
  isExpanded?: boolean;
  ready(): void;
  expand(): void;
  setBackgroundColor(color: string): void;
  setHeaderColor(color: string): void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

const webApp = (): TelegramWebApp | undefined => window.Telegram?.WebApp;

export const isTelegram = (): boolean => Boolean(webApp()?.initData);

/**
 * The credential sent on every request.
 *
 * Outside Telegram this falls back to `VITE_DEV_INIT_DATA` **in dev builds
 * only**. That variable holds a payload signed with the real bot token, which
 * is the only way to exercise the app in a desktop browser — and precisely why
 * the fallback must not survive into a production bundle. `import.meta.env.DEV`
 * is statically replaced at build time, so the branch is removed entirely
 * rather than merely being unreachable: a production build has no code path
 * that reads an initData the app did not get from Telegram.
 */
export function getInitData(): string | null {
  const fromTelegram = webApp()?.initData;
  if (fromTelegram) return fromTelegram;

  if (import.meta.env.DEV) {
    const fromEnv = import.meta.env['VITE_DEV_INIT_DATA'] as string | undefined;
    if (fromEnv) return fromEnv;
    const fromQuery = new URLSearchParams(window.location.search).get('initData');
    if (fromQuery) return fromQuery;
  }
  return null;
}

/**
 * Telegram's language hint, used only to pick a message catalogue.
 *
 * Safe to take from the unverified payload precisely because the consequence of
 * a lie is seeing the wrong language. Nothing else reads `initDataUnsafe`.
 */
export function languageHint(): string | undefined {
  return webApp()?.initDataUnsafe?.user?.language_code;
}

/**
 * The `startapp` payload from a bot deep link, if this launch came from one.
 *
 * The bot builds links like `t.me/<bot>/<app>?startapp=round_<id>` or
 * `?startapp=wallet` (bot/messages.ts); Telegram delivers the value to the app
 * as `initData.start_param`. The app navigates to the round or wallet
 * accordingly in `App.tsx`. Like `languageHint` it is a navigation hint, not a
 * credential — the app only trusts it to pick a screen, never to decide
 * anything about who the player is. `start_param` is only present on the launch
 * that came from a link, so there is nothing to clear on later navigations.
 */
export function startParam(): string | undefined {
  return webApp()?.initDataUnsafe?.start_param;
}

/**
 * Copies Telegram's palette onto CSS custom properties.
 *
 * The Mini App should look like part of the client it is running inside, and
 * the user may switch themes mid-session — so this re-runs on `themeChanged`
 * rather than being read once at boot. Every value has a fallback, because a
 * missing theme key must degrade to a readable colour rather than to
 * `undefined` and an invisible label.
 */
function applyTheme(): void {
  const app = webApp();
  const theme = app?.themeParams ?? {};
  const dark = app?.colorScheme === 'dark';

  const vars: Record<string, string> = {
    '--tg-bg': theme.bg_color ?? (dark ? '#17212b' : '#ffffff'),
    '--tg-text': theme.text_color ?? (dark ? '#f5f5f5' : '#000000'),
    '--tg-hint': theme.hint_color ?? (dark ? '#7d8b99' : '#707579'),
    '--tg-link': theme.link_color ?? (dark ? '#6ab3f3' : '#2481cc'),
    '--tg-button': theme.button_color ?? (dark ? '#5288c1' : '#2481cc'),
    '--tg-button-text': theme.button_text_color ?? '#ffffff',
    '--tg-secondary-bg': theme.secondary_bg_color ?? (dark ? '#232e3c' : '#f4f4f5'),
    '--tg-section-bg': theme.section_bg_color ?? (dark ? '#17212b' : '#ffffff'),
    '--tg-accent': theme.accent_text_color ?? (dark ? '#6ab3f3' : '#2481cc'),
    '--tg-destructive': theme.destructive_text_color ?? '#e53935',
  };

  for (const [key, value] of Object.entries(vars)) {
    document.documentElement.style.setProperty(key, value);
  }
  document.documentElement.dataset['theme'] = dark ? 'dark' : 'light';
}

/**
 * The stable viewport height as a CSS variable.
 *
 * `100vh` is wrong inside a Telegram webview — it does not account for the
 * native header, so a full-height layout ends up scrolled or clipped.
 * `viewportStableHeight` excludes the transient keyboard, which is what a game
 * board wants: the layout should not jump when a keyboard opens somewhere else.
 */
function applyViewport(): void {
  const height = webApp()?.viewportStableHeight;
  document.documentElement.style.setProperty(
    '--tg-viewport-height',
    height ? `${height}px` : '100dvh',
  );
}

export function initTelegram(): void {
  const app = webApp();
  applyTheme();
  applyViewport();

  if (!app) return;

  // Deliberately **not** `ready()` here. Telegram keeps its native loading
  // placeholder on screen until `ready()` is called, and the branded splash
  // (Splash.tsx) calls it the moment it is mounted — so the placeholder gives
  // way to the splash, never to a blank page. `expand()` is fine up front: it
  // only changes the sheet height, it does not dismiss the loader.
  app.expand();

  app.onEvent('themeChanged', applyTheme);
  app.onEvent('viewportChanged', applyViewport);
}

/**
 * Tells Telegram the app has painted its first frame, dismissing the native
 * loading placeholder. Called by the splash once it is on screen.
 */
export function notifyReady(): void {
  webApp()?.ready();
}

/**
 * The Mini App's visible background while the native loader is up. Kept in
 * lockstep with whatever the first frame paints — currently the splash's
 * `--splash-bg` — so Telegram's placeholder hands off without a colour
 * change; the loader *is* the first frame.
 */
export function setBackgroundColor(color: string): void {
  webApp()?.setBackgroundColor?.(color);
}

/**
 * The native header bar. Accepts a hex colour or one of Telegram's keywords
 * (`'bg_color'`, `'secondary_bg_color'`).
 *
 * Hex support landed in Bot API 6.9; older clients reject an arbitrary colour
 * by *throwing* `WebAppHeaderColorError`, which optional chaining does not
 * catch. Unguarded, that throw propagates out of the splash's effect and takes
 * down the first frame the player sees — a blank screen instead of a slightly
 * mismatched header bar. So a rejected colour falls back to the keyword, which
 * every client has always understood.
 */
export function setHeaderColor(color: string): void {
  const app = webApp();
  if (!app?.setHeaderColor) return;
  try {
    app.setHeaderColor(color);
  } catch {
    if (color !== 'bg_color') {
      try {
        app.setHeaderColor('bg_color');
      } catch {
        // Nothing left to try; the header keeps whatever colour it had.
      }
    }
  }
}

/** Shows Telegram's native back button while `onBack` is set. */
export function useNativeBack(onBack: (() => void) | null): void {
  const app = webApp();
  const button = app?.BackButton;
  if (!button) return;

  if (!onBack) {
    button.hide();
    return;
  }
  button.onClick(onBack);
  button.show();
}

/**
 * Haptics. Silently absent on desktop and on older clients, which is why every
 * call is optional-chained rather than feature-detected at the call site.
 */
export const haptics = {
  ball(): void {
    webApp()?.HapticFeedback?.impactOccurred('light');
  },
  win(): void {
    webApp()?.HapticFeedback?.notificationOccurred('success');
  },
  lose(): void {
    webApp()?.HapticFeedback?.notificationOccurred('warning');
  },
  tap(): void {
    webApp()?.HapticFeedback?.impactOccurred('medium');
  },
};
